import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "i18n";
import {
  Head,
  Spinner,
  generateBreadcrumbsData,
  AddOnPurchaseModal,
  NotificationToastTypes,
  NotificationToast,
  useGetSubscriptionActions,
  FETCH_SUBSCRIPTION_ACTIONS,
  AddOnBaseCode,
} from "component-library";
import { AddOnEntitlements } from "shared-types";
import {
  checkIsPerformer,
  doesUserHaveEntitlements,
  getUserGroup,
} from "shared-auth";
import { dataTestIds } from "data-testids";
import { usePagePermission, useReturnUrl } from "shared-hooks";
import {
  cancelAddOn,
  getRecurlyDetails,
  IBillingApiResponse,
  MY_SUBSCRIPTION_QK,
  CANCEL_ADD_ON_MUTATION_KEY,
  REINSTATE_ORIGINAL_PLAN_KEY,
  reinstateOriginalPlan,
} from "services/api/BillingApi/BillingApi";
import PageWrapperWithinLayout from "app-shell/PageWrapperWithinLayout/PageWrapperWithinLayout";
import applicationMap, { CheckoutType } from "services/applicationMap";
import CancellationModal from "components/Modals/CancellationModal";
import ReactivateModal from "components/Modals/ReactivateModal";
import ReinstateOriginalPlanModal from "components/Modals/ReinstateOriginalPlanModal";
import CancelAddOnModal from "components/Modals/CancelAddOnModal";
import { PlanCodePeriod } from "component-library/src/components/Organisms/Billing/types";
import { useUpdateDetailsPopUp } from "app/hooks/useUpdateDetailsPopUp";
import checkIsValidAddress from "helpers/checkIsValidAddress";
import {
  AddOnCalculatedStatus,
  BreadcrumbSection,
  breadcrumbs,
} from "app/constants";
import {
  FEATURE_FLAGS,
  useLaunchDarklyFlags,
  HeapCustomEvent,
  HeapService,
} from "shared-services";
import { AddOnCodes } from "component-library/src/components/Organisms/Billing/constants";
import { pushToDataLayer } from "app-shell/GoogleTagManager/GoogleTagManager";
import {
  AccountTypeBillingTypeMap,
  SHOW_SUBSCRIPTION_CHANGE_SUCCESS_PARAM,
} from "config/common";
import SettingSection from "components/SettingSection/SettingSection";
import { TRACKING_EVENT_TYPE } from "app-shell/GoogleTagManager/constants";
import TransferToGrads from "./TransferToGrads";
import SubscriptionData from "./SubscriptionData";
import { getCheckoutLink } from "helpers/getCheckoutLink";
import ReinstateModal from "components/Modals/ReinstateModal";
import { SubscriptionCodes } from "component-library/src/components/Organisms/Billing/components/AddOnCheckout/SubscriptionPage/constants";
import getLongDate from "helpers/getLongDate";
import { REINSTATE_SEARCH_PARAM } from "pages/IndexPage/constants/constants";

const MySubscriptions = () => {
  usePagePermission([checkIsPerformer], applicationMap.routes.index());
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const history = useHistory();
  const reinstate = new URLSearchParams(history.location.search).get(
    REINSTATE_SEARCH_PARAM
  );
  const subscriptionChangeSuccess = new URLSearchParams(
    history.location.search
  ).get(SHOW_SUBSCRIPTION_CHANGE_SUCCESS_PARAM);
  const [selectedAddon, setSelectedAddon] = useState<AddOnCodes>();
  const [isReinstatePlanModalOpen, setIsReinstatePlanModalOpen] = useState(
    reinstate === "true"
  );
  const [
    isReinstateOriginalPlanModalOpen,
    setIsReinstateOriginalPlanModalOpen,
  ] = useState(false);
  const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = useState(false);
  const [revertModalIsOpen, setRevertModalIsOpen] = useState(false);
  const [isCancelAddOnModalOpen, setIsCancelAddOnModalOpen] = useState(false);
  const [toastState, setToastState] = useState({
    isOpen: false,
    message: "",
    type: NotificationToastTypes.Success,
  });
  const [addOnsToCancel, setAddOnsToCancel] = useState<AddOnCodes[]>([]);
  const { redirectWithReturnUrl } = useReturnUrl();

  const { [FEATURE_FLAGS.ConvertToGrad]: isConvertToGradFlagEnabled } =
    useLaunchDarklyFlags([FEATURE_FLAGS.ConvertToGrad]);

  // errors caught by boundary
  const {
    data: mySubscriptionData,
    isLoading,
    refetch,
  } = useQuery<IBillingApiResponse | undefined>({
    queryKey: MY_SUBSCRIPTION_QK,
    queryFn: async () => await getRecurlyDetails(),
    cacheTime: 0,
  });

  const { mutate, isLoading: isCancelAddOnsLoading } = useMutation({
    mutationKey: CANCEL_ADD_ON_MUTATION_KEY,
    mutationFn: cancelAddOn,
    onSuccess: async () => {
      setIsCancelAddOnModalOpen(false);

      await Promise.all([
        queryClient.invalidateQueries(MY_SUBSCRIPTION_QK),
        queryClient.invalidateQueries(FETCH_SUBSCRIPTION_ACTIONS),
      ]);
    },
  });

  const {
    mutate: mutateReinstateOriginalPlan,
    isLoading: isReinstateOriginalPlanLoading,
  } = useMutation({
    mutationKey: REINSTATE_ORIGINAL_PLAN_KEY,
    mutationFn: reinstateOriginalPlan,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries(MY_SUBSCRIPTION_QK),
        queryClient.invalidateQueries(FETCH_SUBSCRIPTION_ACTIONS),
      ]);
    },
    onError: () => {
      setIsReinstateOriginalPlanModalOpen(false);
    },
  });

  const { data: subActionsData, isLoading: subActionsIsLoading } =
    useGetSubscriptionActions(true);

  useEffect(() => {
    if (subscriptionChangeSuccess) {
      setToastState((toastState) => ({
        ...toastState,
        isOpen: true,
        message: t("performer:accountSettings.changedSubscription.success"),
      }));
    }
  }, [subscriptionChangeSuccess]);

  const { subscription, address, addOns } = mySubscriptionData || {};

  const isReactivation = subActionsData?.isEligibleToReactivate;
  const isValidAddress = checkIsValidAddress(address);
  const renewalLink = getCheckoutLink({
    planCode: subscription?.planCode,
  });
  const reactivationLink = getCheckoutLink({
    planCode: subscription?.planCode,
    checkoutType: CheckoutType.Reactivation,
  });
  const targetLink = isReactivation ? reactivationLink : renewalLink;
  const { showUpdateDetailsPopUp } = useUpdateDetailsPopUp(() =>
    redirectWithReturnUrl(applicationMap.routes.settingsMyDetails(), targetLink)
  );

  // ADD ON SECTION CONDITIONS
  const isMainPerformer = checkIsPerformer();

  const goToMyAccount = () =>
    history.replace(applicationMap.routes.accountSettings());

  const addOnSuccessCallback = async (trackingData: any = {}) => {
    const userGroup = getUserGroup();

    pushToDataLayer({
      event: TRACKING_EVENT_TYPE.AddOnPurchase,
      memberType:
        AccountTypeBillingTypeMap[
          userGroup as keyof typeof AccountTypeBillingTypeMap
        ],
      ...trackingData,
    });
    await refetch();
    setSelectedAddon(undefined);
  };

  const handleCancelPlan = () => {
    HeapService.track(HeapCustomEvent.PerformerCancelSubscription);
    setIsCancelPlanModalOpen(true);
  };

  if (isLoading || subActionsIsLoading) return <Spinner />;

  const hasAddOnBundle = doesUserHaveEntitlements(
    AddOnEntitlements.AddOnBundle
  );

  const isRenewalPeriod = subActionsData?.isEligibleToRenew;

  const isMonthlyPayingPremiumUser =
    hasAddOnBundle && subscription?.planCode.includes(PlanCodePeriod.Monthly);
  const isAnnuallyPayingPremiumRenewalPeriod =
    hasAddOnBundle &&
    isRenewalPeriod &&
    subscription?.planCode.includes(PlanCodePeriod.Annual);
  const isPrimaryButtonEnabled =
    !hasAddOnBundle ||
    isMonthlyPayingPremiumUser ||
    isAnnuallyPayingPremiumRenewalPeriod;

  const shouldShowTransferToGrads =
    isMainPerformer && isConvertToGradFlagEnabled;

  const generatePrimaryButtonLabel = () => {
    if (hasAddOnBundle) {
      return t("common:button.label.changePlan");
    }

    if (isReactivation) {
      return t("common:button.label.reactivateSubscription");
    }

    if (isRenewalPeriod) {
      return t("common:button.label.renewSubscription");
    }

    return "";
  };

  const handleAddressInvalidRedirect = () => {
    if (!isValidAddress) {
      showUpdateDetailsPopUp();

      return false;
    }

    return true;
  };

  const primaryButtonProps = {
    label: generatePrimaryButtonLabel(),
    isDisabled: !isPrimaryButtonEnabled,
    onClick: () => {
      if (handleAddressInvalidRedirect()) history.push(targetLink);
    },
    isHidden: !isReactivation && !isRenewalPeriod,
  };

  const handleReinstateOriginalPlan = () => {
    if (isReinstateOriginalPlanLoading) {
      return;
    }

    mutateReinstateOriginalPlan();
  };

  const handleChangePlan = (planCode?: string) => {
    if (handleAddressInvalidRedirect()) {
      HeapService.track(HeapCustomEvent.PerformerChangePlan);

      const currentAddons = addOns?.filter(
        (addOn) => addOn.calculatedStatus === AddOnCalculatedStatus.Active
      );

      const baseAddOns = Object.values(AddOnBaseCode).filter((baseAddOnCode) =>
        currentAddons?.some((addOn) => addOn.code.includes(baseAddOnCode))
      );

      history.push(
        getCheckoutLink({
          planCode,
          checkoutType: CheckoutType.Change,
          addOns: baseAddOns,
        })
      );
    }
  };

  const activeAddOns =
    mySubscriptionData?.addOns
      ?.filter(
        (addOn) => addOn.calculatedStatus === AddOnCalculatedStatus.Active
      )
      .map((filteredAddOn) => filteredAddOn.code) || [];

  const formattedDate = subscription?.currentTermEndsAt
    ? getLongDate(subscription?.currentTermEndsAt)
    : "-";

  const handleSubscriptionCancelled = () => {
    HeapService.track(HeapCustomEvent.PerformerCancelSubscriptionModal);

    setIsCancelPlanModalOpen(false);
    setToastState({
      ...toastState,
      isOpen: true,
      message: t("performer:accountSettings.mySubscriptionPage.canceled.toast"),
    });
  };

  const handleSubscriptionReinstated = () => {
    setIsReinstatePlanModalOpen(false);
    setToastState({
      ...toastState,
      isOpen: true,
      message: t(
        "performer:accountSettings.mySubscriptionPage.reinstated.toast"
      ),
    });
  };

  const handleCancelAddOnClick = (addOnCode: string) => {
    setIsCancelAddOnModalOpen(true);
    setAddOnsToCancel([addOnCode as AddOnCodes]);
  };

  return (
    <>
      <Head>
        <title data-testid={dataTestIds.userAccountApp}>
          {t("performer:accountSettings.mySubscription.header")}
        </title>
      </Head>
      <PageWrapperWithinLayout
        pageHeader={t("performer:accountSettings.mySubscription.header")}
        subFooter={{
          text: t("common:button.label.close"),
          onClick: goToMyAccount,
        }}
        breadcrumbs={generateBreadcrumbsData(breadcrumbs, [
          BreadcrumbSection.MyAccount,
          BreadcrumbSection.AccountSettings,
        ])}
      >
        <SettingSection
          sectionDescription={t(
            "performer:accountSettings.mySubscriptionPage.description"
          )}
          subSectionHeader={t(
            "common:accountSettings.mySubscription.subSection.title"
          )}
          actionButtonLabel={primaryButtonProps.label}
          actionButtonOnClick={primaryButtonProps.onClick}
          actionButtonDisabled={primaryButtonProps.isDisabled}
          actionButtonHidden={primaryButtonProps.isHidden}
        >
          <SubscriptionData
            handleCancelPlan={handleCancelPlan}
            handleChangePlan={handleChangePlan}
            handleReinstatePlan={() => setIsReinstatePlanModalOpen(true)}
            handleReinstateOriginalPlan={() =>
              setIsReinstateOriginalPlanModalOpen(true)
            }
            subscription={subscription}
            addOns={addOns}
            onBuyAddOnClick={(code) => setSelectedAddon(code)}
            onCancelAddOnClick={handleCancelAddOnClick}
            formattedDate={formattedDate}
            subActionsData={subActionsData}
          />
          {shouldShowTransferToGrads ? (
            <>
              <hr className="mt-2.5 mb-5" />
              <TransferToGrads
                enabled={subActionsData?.isEligibleForGradTransfer}
              />
            </>
          ) : undefined}
        </SettingSection>

        <CancellationModal
          isOpen={isCancelPlanModalOpen}
          setSubscriptionSuccessCancelled={handleSubscriptionCancelled}
          closePopUp={() => setIsCancelPlanModalOpen(false)}
          formattedDate={formattedDate}
        />
        <ReinstateModal
          isOpen={isReinstatePlanModalOpen}
          closePopUp={() => setIsReinstatePlanModalOpen(false)}
          onSuccessCallback={handleSubscriptionReinstated}
          planCode={subscription?.planCode as SubscriptionCodes}
          formattedDate={formattedDate}
          activeAddOns={activeAddOns}
        />
        <ReactivateModal
          isOpen={revertModalIsOpen}
          closePopUp={() => setRevertModalIsOpen(false)}
          onSuccessCallback={refetch}
        />

        <ReinstateOriginalPlanModal
          isOpen={isReinstateOriginalPlanModalOpen}
          isLoading={isReinstateOriginalPlanLoading}
          onClosePopup={() => setIsReinstateOriginalPlanModalOpen(false)}
          onSuccessCallback={handleReinstateOriginalPlan}
          planCode={subscription?.pendingSubscription?.planCode ?? ""}
        />

        <CancelAddOnModal
          isOpen={isCancelAddOnModalOpen}
          closePopUp={() => setIsCancelAddOnModalOpen(false)}
          isCancelAddOnLoading={isCancelAddOnsLoading}
          onSubmitCancelAddOn={() =>
            mutate({
              subscriptionId: mySubscriptionData?.subscription.id || "",
              addons: addOnsToCancel,
            })
          }
        />
        <AddOnPurchaseModal
          selectedAddOnCode={selectedAddon}
          closePopUp={() => setSelectedAddon(undefined)}
          onSuccessCallback={addOnSuccessCallback}
        />
        <NotificationToast
          className="sticky left-0 right-0 bottom-[80px] min-w-[300px] w-content m-auto"
          show={toastState.isOpen}
          text={toastState.message}
          type={toastState.type}
          handleHide={() => setToastState({ ...toastState, isOpen: false })}
        />
      </PageWrapperWithinLayout>
    </>
  );
};

export default MySubscriptions;
